import { getApi } from '../../providers/AuthContext';
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Users, AlertCircle, X, Search, Sparkles } from 'lucide-react';
import PageWrapper from '../../components/PageWrapper';
import ChildCard from '../../components/Children/Card';

const SkeletonCard = () => (
  <div className="rounded-2xl overflow-hidden bg-card border border-border/40">
    <div className="h-1.5 bg-gradient-to-r from-muted via-muted-foreground/10 to-muted" />
    <div className="p-6 flex flex-col items-center">
      {/* Avatar skeleton */}
      <div className="skeleton w-24 h-24 rounded-full mb-4" />
      {/* Name skeleton */}
      <div className="skeleton h-5 w-28 mb-2 rounded-lg" />
      {/* Bio skeleton */}
      <div className="skeleton h-3 w-40 mb-1 rounded-lg" />
      <div className="skeleton h-3 w-32 rounded-lg" />
    </div>
    {/* Action bar skeleton */}
    <div className="border-t border-border/30 p-3 flex justify-center gap-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="skeleton h-8 w-8 rounded-lg" />
      ))}
    </div>
  </div>
);

const EmptyState = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="flex flex-col items-center justify-center py-20 px-6"
  >
    {/* Decorative background glow */}
    <div className="relative mb-8">
      <div className="absolute -inset-6 rounded-full bg-gradient-to-tr from-primary/20 via-accent/10 to-secondary/20 blur-2xl" />
      <div className="relative w-24 h-24 rounded-3xl bg-gradient-to-br from-primary/10 to-accent/10 dark:from-primary/20 dark:to-accent/20 flex items-center justify-center border border-primary/10">
        <Users className="w-12 h-12 text-primary" />
      </div>
    </div>
    <h3 className="text-2xl font-bold text-foreground mb-3">No children yet</h3>
    <p className="text-muted-foreground text-center max-w-md mb-10 leading-relaxed text-base">
      Start by creating a profile for your child. Their profile is linked via QR code to the trading cards you create — it's like a digital identity card!
    </p>
    <Link
      to="/children/new"
      className="btn-primary py-3.5 px-10 no-underline text-center gap-2.5 text-base font-semibold shadow-soft-md hover:shadow-soft-lg transition-all duration-300"
    >
      <Sparkles className="w-5 h-5" />
      Add Your First Child
    </Link>
  </motion.div>
);

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const item = {
  hidden: { opacity: 0, y: 24, scale: 0.96 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] } }
};

const Page: React.FC = () => {
  const api = getApi();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadChildren = async () => {
    try {
      setError(null);
      const result = await api.get('/api/v1/children');
      setChildren(result.children);
    } catch (ex: any) {
      setError('Failed to load children.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChildren();
  }, []);

  const deleteChild = async (child_id: string) => {
    try {
      await api.delete(`/api/v1/children/${child_id}`);
      loadChildren();
    } catch (ex: any) {
      const msg = ex.response?.error || 'Failed to delete child.';
      setError(msg);
    }
  };

  const filteredChildren = useMemo(() => {
    if (!searchQuery.trim()) return children;
    const q = searchQuery.toLowerCase();
    return children.filter((child: any) =>
      child.first_name?.toLowerCase().includes(q) ||
      child.bio?.toLowerCase().includes(q)
    );
  }, [children, searchQuery]);

  const showContent = !loading && children.length > 0;

  return (
    <PageWrapper pageTitle="Children">
      {/* Page header with count + actions */}
      <div className="px-4 pt-5 pb-2 lg:px-6 lg:pt-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground leading-tight">
                {loading ? 'Children' : `${children.length} ${children.length === 1 ? 'Child' : 'Children'}`}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">Profiles linked to cards via QR code</p>
            </div>
          </div>
          {showContent && (
            <Link
              to="/children/new"
              className="btn-primary py-2.5 px-5 no-underline text-center gap-2 text-sm font-medium shrink-0"
            >
              <Plus className="w-4 h-4" />
              Add Child
            </Link>
          )}
        </div>

        {/* Search bar (when there are children) */}
        {showContent && children.length > 2 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="mt-4"
          >
            <div className="relative max-w-sm">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Search children..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="form-input pl-10 py-2.5 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-md text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* Error state */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mx-4 mt-3 lg:mx-6"
          >
            <div className="p-4 bg-destructive/10 dark:bg-destructive/20 text-destructive border border-destructive/20 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span className="flex-1 text-sm font-medium">{error}</span>
              <button
                onClick={() => setError(null)}
                className="p-1 rounded-lg hover:bg-destructive/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 p-4 lg:p-6">
          {[0, 1, 2, 3].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : children.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Filtered results info */}
          {searchQuery && (
            <div className="px-4 lg:px-6 pt-2">
              <p className="text-sm text-muted-foreground">
                {filteredChildren.length === 0
                  ? `No results for "${searchQuery}"`
                  : `Showing ${filteredChildren.length} of ${children.length}`
                }
              </p>
            </div>
          )}

          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 p-4 lg:p-6"
          >
            {filteredChildren.map((child: any, index: number) => (
              <motion.div key={child.id} variants={item}>
                <ChildCard child={child} deleteAction={deleteChild} index={index} />
              </motion.div>
            ))}
          </motion.div>
        </>
      )}
    </PageWrapper>
  );
};

export default Page;
